import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <section className="gradient-hero py-12 border-b border-border">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-4" variant="outline">
              Legal
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-foreground mb-4">
              Terms of Service
            </h1>
            <p className="text-muted-foreground">Last updated: January 2025</p>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          <Card className="max-w-4xl mx-auto">
            <CardContent className="p-8 md:p-12 prose prose-slate max-w-none">
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-4">1. Acceptance of Terms</h2>
                  <p className="text-muted-foreground">
                    By accessing and using wealthcalc ("the Service"), you accept and agree to be bound by these Terms
                    of Service. If you do not agree to these terms, please do not use the Service.
                  </p>
                </div>

                <Separator />

                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-4">2. Description of Service</h2>
                  <p className="text-muted-foreground">
                    wealthcalc provides free online financial calculators and tools for personal and professional use.
                    All calculations are performed in your browser, and we do not store your financial data.
                  </p>
                </div>

                <Separator />

                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-4">3. Use of Service</h2>
                  <h3 className="text-xl font-semibold text-foreground mb-3">3.1 Permitted Use</h3>
                  <p className="text-muted-foreground mb-4">
                    You may use our calculators for lawful purposes only. You agree not to:
                  </p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2">
                    <li>Use the Service for any illegal purpose</li>
                    <li>Attempt to gain unauthorized access to our systems</li>
                    <li>Interfere with or disrupt the Service</li>
                    <li>Copy, modify, or distribute our content without permission</li>
                    <li>Use automated systems to access the Service</li>
                  </ul>
                </div>

                <Separator />

                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-4">4. Accuracy of Information</h2>
                  <p className="text-muted-foreground">
                    While we strive to provide accurate calculators and information, we do not guarantee the accuracy,
                    completeness, or reliability of any calculations or content. All results should be verified
                    independently before making financial decisions.
                  </p>
                </div>

                <Separator />

                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-4">5. Disclaimer of Warranties</h2>
                  <p className="text-muted-foreground mb-3">
                    THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND. WE DISCLAIM ALL
                    WARRANTIES, INCLUDING:
                  </p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2">
                    <li>Warranties of merchantability</li>
                    <li>Fitness for a particular purpose</li>
                    <li>Non-infringement</li>
                    <li>Accuracy or reliability of results</li>
                  </ul>
                </div>

                <Separator />

                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-4">6. Limitation of Liability</h2>
                  <p className="text-muted-foreground">
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
                    SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR OTHER INTANGIBLE
                    LOSSES, RESULTING FROM YOUR USE OF THE SERVICE.
                  </p>
                </div>

                <Separator />

                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-4">7. Intellectual Property</h2>
                  <p className="text-muted-foreground">
                    All content on wealthcalc, including text, graphics, logos, and software, is the property of
                    wealthcalc or its licensors and is protected by copyright and other intellectual property laws.
                  </p>
                </div>

                <Separator />

                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-4">8. Third-Party Links</h2>
                  <p className="text-muted-foreground">
                    Our Service may contain links to third-party websites or services. We are not responsible for the
                    content, privacy policies, or practices of third-party sites.
                  </p>
                </div>

                <Separator />

                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-4">9. Advertising</h2>
                  <p className="text-muted-foreground">
                    We display advertisements through third-party ad networks like Google AdSense. These advertisers may
                    use cookies and other technologies as described in our Privacy Policy.
                  </p>
                </div>

                <Separator />

                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-4">10. Modifications to Service</h2>
                  <p className="text-muted-foreground">
                    We reserve the right to modify or discontinue the Service at any time without notice. We shall not
                    be liable to you or any third party for any modification, suspension, or discontinuance of the
                    Service.
                  </p>
                </div>

                <Separator />

                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-4">11. Changes to Terms</h2>
                  <p className="text-muted-foreground">
                    We may update these Terms of Service from time to time. Continued use of the Service after changes
                    constitutes acceptance of the new terms.
                  </p>
                </div>

                <Separator />

                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-4">12. Governing Law</h2>
                  <p className="text-muted-foreground">
                    These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in
                    which wealthcalc operates, without regard to conflict of law principles.
                  </p>
                </div>

                <Separator />

                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground mb-4">13. Contact Information</h2>
                  <p className="text-muted-foreground">
                    If you have questions about these Terms of Service, please contact us at:
                    <br />
                    <strong>Email:</strong> legal@wealthcalc.online
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsOfServicePage;
